import { isDailyRashifalHubPath } from "../../../edge-db/shared/rashifal-freshness";
import { canonicalItemUrl } from "../../../shared/canonical-url";
import { APP_AVAILABLE } from "../app-availability";
import {
  CONTENT_REVIEWER_ID,
  activeContentReviewer,
  boundReviewEvidence,
  type ReviewEvidence,
} from "../content-reviewer";
import { PAGE_FRESHNESS } from "../page-freshness.generated";
import { freshnessKey } from "../../../edge-db/shared/page-freshness-key";
import { SITE_LOGO, SITE_NAME, SITE_URL } from "./site";
import { BUSINESS_POSTAL_ADDRESS } from "../business-address";

// App store anchors are entity-graph SAME_AS links (rule 26) — only advertise
// them when the app is publicly installable, else they 404 and weaken trust.
const APP_STORE_SAME_AS = [
  "https://play.google.com/store/apps/details?id=com.ishvaram.app",
  "https://apps.apple.com/app/id6743459479",
];

// 2026-07-27: the Wikidata anchor was REMOVED — Q139596352 was deleted on
// 2026-07-20 by an admin via Requests for Deletions ("Does not meet the
// notability policy"). Rule 26 forbids a SAME_AS that does not resolve: a
// crawler follows it, so a dead anchor is worse than an absent one. Re-add only
// once an entity exists again AND `sync_wikidata_to_schema.py` exits 0 — and
// per rule 26, sequence recreation AFTER press coverage or it fails WD:N again.
//
// Replaced by two owner-confirmed, resolving anchors (LinkedIn + Pinterest), so
// the entity graph is honestly above the G100 >=5 threshold rather than propped
// up by a 404. NOTE: an `x.com/ishvaram` account exists and is titled
// "Ishvaram" but is NOT ours (owner-confirmed 2026-07-27) — never add it. That
// is why an anchor needs owner confirmation, not just an HTTP 200.
export const SAME_AS: string[] = [
  ...(APP_AVAILABLE ? APP_STORE_SAME_AS : []),
  // 2026-09-24: the site's own URL was listed here. `sameAs` names OTHER
  // profiles of the entity; pointing it at itself is a self-reference, not an
  // anchor. Every entry below is an owner-confirmed external profile.
  "https://www.instagram.com/ishvaram.in/",
  "https://www.facebook.com/ishvaram.in",
  "https://www.youtube.com/channel/UCmLGFR2IpfqgoWaHuD-L9Vg",
  "https://www.linkedin.com/company/ishvaram/",
  "https://www.pinterest.com/ishvaramindia/",
  // Owner-confirmed 2026-07-30 (owner set the org avatar; we operate the org):
  // public org profile with README, populated the same day (rule 26).
  "https://github.com/Ishvaram-Business",
  // Owner-created + confirmed 2026-07-30. NOTE for the liveness guard: Crunchbase
  // 403s datacenter probes (bot wall) — that is the documented INCONCLUSIVE
  // class, never a dead-anchor alarm (rule 26).
  "https://www.crunchbase.com/organization/ishvaram",
  // Google Business Profile CID link (owner-confirmed 2026-07-30: derived from
  // the owner's live Maps place URL, KG mid /g/11xf9bmq8p; the dashboard
  // "business id" is a DIFFERENT namespace — never anchor that). Maps serves a
  // JS shell to datacenter probes: liveness-guard INCONCLUSIVE class (rule 26).
  "https://maps.google.com/?cid=10448390172209870661",
];

/** The languages the site publishes in (`/` and `/hi/`); see `organizationSchema`. */
export const SITE_LANGUAGES = ["en", "hi"];

/** Stable @id for the founder Person node (`Organization.founder`). */
export const FOUNDER_ID = `${SITE_URL}#founder`;

/**
 * The real founder (owner-confirmed 2026-07-23), who builds the tools and
 * runs the site. Every field is a fact already public on-site: the name and
 * founder role from this Organization node, "operated by Digesh Nanda" on
 * /terms/ and /contact/, and /about/ as the on-site profile. Deliberately NO
 * `sameAs`, NO `jobTitle`, NO `knowsAbout` and NO astrology credential: the
 * credentialed Jyotish reviewer is a different, real person
 * (`content-reviewer.ts`), and the two must never blur (ADR-0022).
 *
 * 2026-09-23: he is no longer the Article `author`. Pages here are computed by
 * the engine and explained by AI-drafted, rule-reviewed copy (the site's own
 * /editorial-policy/), so naming a person as the author of ~3,000 templated
 * pages was a false byline. `ORG_AUTHOR` is the author; this node stays as the
 * Organization's `founder`, which is true.
 */
export const FOUNDER = {
  "@type": "Person",
  "@id": FOUNDER_ID,
  name: "Digesh Nanda",
  url: `${SITE_URL}/about/`,
} as const;

/** Stable @id for the Organization node the layout graph defines. */
export const ORG_ID = `${SITE_URL}#org`;

/**
 * The Article / WebPage `author`: the Organization that publishes the page.
 * Emitted as a small typed node (not a bare `@id` reference) so it defines
 * `#org` on the page itself and never dangles where the layout graph is absent
 * (worker/static emitters); it merges with the full `ORG` node by `@id`.
 */
export const ORG_AUTHOR = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  url: SITE_URL,
} as const;

export const ORG = {
  "@type": "Organization",
  "@id": `${SITE_URL}#org`,
  name: SITE_NAME,
  url: SITE_URL,
  address: BUSINESS_POSTAL_ADDRESS,
  logo: {
    "@type": "ImageObject",
    url: SITE_LOGO,
  },
  // E-E-A-T: the entity's demonstrated expertise domain + its editorial/methodology
  // page (how facts are computed, what the AI never does). Honest, org-level — no
  // fabricated human (content-reviewer.ts stays owner-gated).
  knowsAbout: [
    "Vedic astrology",
    "Jyotish",
    "Kundali",
    "Panchang",
    "Muhurat",
    "Kundli Matching",
    "Dosha analysis",
    "Nakshatra",
    "Vimshottari Dasha",
    "Sade Sati",
    "Kaal Sarp Dosha",
    "Government Job Yoga",
    "Love Marriage Astrology",
    "Ashtakvarga",
    "Shodashvarga Divisional Charts",
    "Swiss Ephemeris Calculations",
  ],
  publishingPrinciples: `${SITE_URL}/about/`,
  // E-E-A-T contact signal (YMYL): a reachable support channel is a documented
  // quality-rater expectation. Email only — the same address already public on
  // /contact/; no fabricated NAP.
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@ishvaram.com",
    availableLanguage: ["en", "hi"],
  },
  // One public, business-wide policy for merchant listings. This mirrors the
  // visible /refund-policy/ terms; reason-specific fees distinguish a normal
  // change-of-mind return from a damaged or incorrect delivery.
  hasMerchantReturnPolicy: {
    "@type": "MerchantReturnPolicy",
    merchantReturnLink: `${SITE_URL}/refund-policy/`,
    applicableCountry: "IN",
    returnPolicyCountry: "IN",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 7,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    customerRemorseReturnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    itemDefectReturnFees: "https://schema.org/FreeReturn",
    refundType: "https://schema.org/FullRefund",
  },
  // Real founder + founding year (owner-confirmed 2026-07-23).
  founder: FOUNDER,
  foundingDate: "2025",
  ...(SAME_AS.length > 0 && { sameAs: SAME_AS }),
} as const;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...ORG,
    description:
      "Vedic astrology tools powered by Swiss Ephemeris — free kundali, rashifal, dosha analysis, muhurat, and AI-guided Jyotish consultations.",
    areaServed: "IN",
    // The site publishes in two languages (`/` and `/hi/`). The old ten-language
    // list claimed Sanskrit (no edition at all) and seven regional languages
    // whose editions are a handful of noindex pages, not a served site
    // (2026-09-24).
    knowsLanguage: SITE_LANGUAGES,
  };
}

export interface BreadcrumbEntry {
  name: string;
  url: string;
}

/** BreadcrumbList with every item normalized through the canonical URL owner. */
export function breadcrumbSchema(trail: BreadcrumbEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((breadcrumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: breadcrumb.name,
      item: canonicalItemUrl(
        breadcrumb.url.startsWith("http") ? breadcrumb.url : `${SITE_URL}${breadcrumb.url}`
      ),
    })),
  };
}

export function buildNestedBreadcrumbs(segments: Array<{ name: string; url: string }>) {
  return breadcrumbSchema(segments);
}

export const CORE_PILLAR_HUBS: Array<{ name: string; path: string }> = [
  { name: "Free Janam Kundali", path: "/free-kundali/" },
  { name: "Kundli Matching & Milan", path: "/kundli-matching/" },
  { name: "Today Panchang & Tithi", path: "/panchang/" },
  { name: "Choghadiya Muhurat", path: "/choghadiya/" },
  { name: "Aaj Ka Rashifal", path: "/aaj-ka-rashifal/" },
  { name: "Shubh Muhurat Timings", path: "/shubh-muhurat/" },
];

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    url: SITE_URL,
    name: SITE_NAME,
    alternateName: "Ishvaram AI Vedic Astrology",
    publisher: { "@id": `${SITE_URL}#org` },
    inLanguage: SITE_LANGUAGES,
    hasPart: CORE_PILLAR_HUBS.map((hub) => ({
      "@type": "WebPage",
      "@id": `${SITE_URL}${hub.path}`,
      url: `${SITE_URL}${hub.path}`,
      name: hub.name,
      isPartOf: { "@id": `${SITE_URL}#website` },
    })),
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * The page language the schema may declare, from the ROUTE first.
 *
 * A `/hi/` URL is a Hindi page whatever the caller passed: until 2026-09-24
 * several Hindi routes reused an English builder (or omitted the field and
 * took the `"en"` default), so Hindi pages declared `inLanguage: "en"`. The
 * route is the one fact that cannot drift from the rendered document. Other
 * URLs keep the caller's value (a Hindi-language page may live at an
 * unprefixed slug), defaulting to English.
 */
export function schemaLanguage(
  url: string | undefined,
  declared: "en" | "hi" | undefined
): "en" | "hi" {
  if (url) {
    const path = url.startsWith(SITE_URL) ? url.slice(SITE_URL.length) : url;
    if (path === "/hi" || path.startsWith("/hi/")) return "hi";
  }
  return declared ?? "en";
}

export interface CollectionPageInput {
  name: string;
  description: string;
  url: string;
  inLanguage?: "en" | "hi";
}

/** CollectionPage schema — for directories/catalogs, never a single-person profile. */
export function collectionPageSchema(input: CollectionPageInput) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: canonicalItemUrl(input.url),
    inLanguage: schemaLanguage(input.url, input.inLanguage),
    isPartOf: { "@id": `${SITE_URL}#website` },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface ArticleInput {
  headline: string;
  description: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  inLanguage?: "en" | "hi";
  speakableSelectors?: string[];
  /**
   * The page renders an `AnswerSummary` block. ONLY then may the schema cite
   * `[data-answer-summary]`.
   *
   * Until 2026-09-21 this schema emitted that selector unconditionally, so 60 of
   * the 92 pages calling `articleSchema` declared a speakable block they never
   * render — including `/hi/free-kundali/`, which absorbs 8 of the top 17
   * question-shaped queries. `SeoPageShell` has always honoured the invariant
   * (`seoPageShellSpeakableJsonLd` emits only when an answer renders); this path
   * did not. The default is now the `h1`, which every page genuinely has.
   */
  hasAnswerSummary?: boolean;
  about?: Array<Record<string, unknown>>;
  /**
   * Completed page-level human-review evidence. Never set this from the global
   * reviewer profile alone; the values must match a published ledger entry
   * (`boundReviewEvidence` in content-reviewer.ts is the one predicate).
   */
  reviewEvidence?: ReviewEvidence;
}

/**
 * Same-day surfaces: their `dateModified` is the ENGINE's IST civil date for
 * today's content (rule 64 same-day contract), which the copy-freshness
 * registry must never replace — `/rashifal/` is registered for its evergreen
 * copy, yet its live gate asserts `"dateModified":"<today>"` daily. The two
 * rashifal hubs come from the one same-day owner the live guard, exporter and
 * edge worker share; the rest are the daily engine routes that pass today's
 * date the same way.
 */
const SAME_DAY_ROUTE_PATHS: ReadonlySet<string> = new Set([
  "/daily-horoscope/",
  "/today-horoscope/",
  "/tomorrow-horoscope/",
  "/panchang/today/",
  "/choghadiya/today/",
  "/rahu-kaal/today/",
]);

function isSameDaySurface(pathname: string): boolean {
  return isDailyRashifalHubPath(pathname) || SAME_DAY_ROUTE_PATHS.has(pathname);
}

/**
 * Rule 48 date truth: when the route is registered in the git-derived
 * `PAGE_FRESHNESS` projection, that evidence date IS the Article
 * `dateModified` — a caller-supplied literal can never override it, and a
 * registered page's schema date and visible note cannot drift. Unregistered
 * routes, and same-day surfaces whose date is the engine's, fall back to
 * whatever the caller supplied (or nothing).
 */
export function freshnessDateModified(url: string | undefined): string | undefined {
  if (!url?.startsWith(SITE_URL)) return undefined;
  const route = url.slice(SITE_URL.length).replace(/\/+$/, "") || "/";
  if (isSameDaySurface(`${route}/`)) return undefined;
  // Shared resolver: dynamic families (festival, muhurat) read their family
  // key, so a detail page and its sitemap row cannot disagree (rule 48).
  return (PAGE_FRESHNESS as Record<string, string>)[freshnessKey(route)];
}

/** Evidence dates only: publication as supplied, modification from the freshness owner. */
function articleDates(input: ArticleInput) {
  const dateModified = freshnessDateModified(input.url) ?? input.dateModified;
  return {
    ...(input.datePublished && { datePublished: input.datePublished }),
    ...(dateModified && { dateModified }),
  };
}

/** The page-level review claim — present only for a ledger-bound record. */
function articleReviewClaim(input: ArticleInput) {
  const boundReview = activeContentReviewer() ? boundReviewEvidence(input.reviewEvidence) : null;
  if (!boundReview) return {};
  return {
    reviewedBy: { "@id": CONTENT_REVIEWER_ID },
    dateReviewed: boundReview.reviewedAt.slice(0, 10),
  };
}

/** Article schema — for explanatory / editorial pages. */
export function articleSchema(input: ArticleInput) {
  const url = input.url ? canonicalItemUrl(input.url) : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    ...(url && { url, mainEntityOfPage: url }),
    ...(input.image && { image: input.image }),
    ...articleDates(input),
    inLanguage: schemaLanguage(url, input.inLanguage),
    // The publishing Organization, not a person: see `FOUNDER` for why.
    author: ORG_AUTHOR,
    ...(input.about && { about: input.about }),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector:
        input.speakableSelectors ??
        (input.hasAnswerSummary ? ["[data-answer-summary]", "h1"] : ["h1"]),
    },
    ...articleReviewClaim(input),
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface ChartImageObjectInput {
  title: string;
  imageUrl: string;
  caption?: string;
  width?: number;
  height?: number;
  representativeOfPage?: boolean;
}

/** Schema.org ImageObject for visual charts, Kundali diagrams, and Google Images rich results. */
export function chartImageObjectSchema(input: ChartImageObjectInput) {
  const fullUrl = input.imageUrl.startsWith("http")
    ? input.imageUrl
    : `${SITE_URL}${input.imageUrl.startsWith("/") ? "" : "/"}${input.imageUrl}`;
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: fullUrl,
    url: fullUrl,
    name: input.title,
    ...(input.caption && { caption: input.caption }),
    ...(input.width && { width: input.width }),
    ...(input.height && { height: input.height }),
    ...(input.representativeOfPage !== undefined && {
      representativeOfPage: input.representativeOfPage,
    }),
  };
}

export interface ToolWebApplicationInput {
  name: string;
  description: string;
  url: string;
  applicationCategory?: string;
  operatingSystem?: string;
  isFree?: boolean;
}

/** Schema.org WebApplication with CalculateAction for interactive Vedic calculators. */
export function toolWebApplicationSchema(input: ToolWebApplicationInput) {
  const fullUrl = input.url.startsWith("http")
    ? input.url
    : `${SITE_URL}${input.url.startsWith("/") ? "" : "/"}${input.url}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: input.name,
    description: input.description,
    url: fullUrl,
    applicationCategory: input.applicationCategory ?? "AstrologyApplication",
    operatingSystem: input.operatingSystem ?? "All",
    browserRequirements: "Requires JavaScript. Requires HTML5.",
    isPartOf: { "@id": `${SITE_URL}#website` },
    potentialAction: {
      "@type": "CalculateAction",
      name: `Calculate ${input.name}`,
      target: fullUrl,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
    },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

const SCHEMA_TEXT_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  hellip: "...",
  laquo: '"',
  ldquo: '"',
  lsaquo: "'",
  lsquo: "'",
  lt: "<",
  mdash: "-",
  ndash: "-",
  nbsp: " ",
  quot: '"',
  raquo: '"',
  rdquo: '"',
  rsaquo: "'",
  rsquo: "'",
};

function decodeSchemaTextEntities(value: string): string {
  return value.replace(/&(#\d+|#x[\da-f]+|[a-z][\da-z]+);/gi, (entity, rawName: string) => {
    const name = rawName.toLowerCase();
    if (name.startsWith("#x")) {
      const codePoint = Number.parseInt(name.slice(2), 16);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    if (name.startsWith("#")) {
      const codePoint = Number.parseInt(name.slice(1), 10);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    return SCHEMA_TEXT_ENTITIES[name] ?? entity;
  });
}

function plainSchemaText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = decodeSchemaTextEntities(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0 ? text : null;
}

/**
 * FAQPage schema — the single highest-ROI AEO primitive.
 *
 * Rules:
 * - Q and A must be plain text (strip HTML from answer before passing in).
 * - Each answer should be a self-contained, quotable chunk (2–4 sentences).
 * - Every Q&A should contain the page's primary keyword variant so FAQ
 *   additions don't dilute topical focus.
 */
export function faqPageSchema(faqs: FAQItem[]) {
  const seenQuestions = new Set<string>();
  const mainEntity = faqs.flatMap((f) => {
    const question = plainSchemaText(f.question);
    const answer = plainSchemaText(f.answer);
    if (!question || !answer || seenQuestions.has(question)) return [];
    seenQuestions.add(question);
    return [
      {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      },
    ];
  });

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

// No HowTo builder: Google retired the HowTo rich result and an audit found
// HowTo steps that were not visible on the page, which breaks the
// structured-data content-match policy. Step lists stay as on-page UI only.

/**
 * AEO & Entity Authority Schema.
 *
 * Describes the same publisher entity as the site-wide Organization graph.
 */
export function aeoEntityAuthoritySchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}#org`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_LOGO,
    sameAs: SAME_AS,
    publishingPrinciples: `${SITE_URL}/about/`,
    knowsAbout: [
      "Vedic Astrology",
      "Brihat Parashara Hora Shastra",
      "Swiss Ephemeris Astronomy",
      "Panchang Calculations",
      "Kundli Matching & Gun Milan",
      "Vimshottari Dasha Systems",
    ],
  };
}
